import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Drawer, Popconfirm, Row, Space, Table, Typography, Card, Tooltip, Tag, Input, Select, Switch, DatePicker } from 'antd';
import type { TableProps } from 'antd';
import type { FilterValue, SortOrder, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import { openNotification } from '../../../utils';
import { Link, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import {
  listInspectors,
  deleteInspector,
  updateInspectorStatus,
  updateInspectorAccreditationValidator,
  listInspectorsAll,
} from '../services/inspectorService';
import { listProfessionsAll } from '../../professions/services/professionService';
import { apiFetch } from 'services/apiClient';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import InspectorForm from './Form';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function InspectorList({ referenceId }: { referenceId?: number }) {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [accreditationSwitchLoadingId, setAccreditationSwitchLoadingId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [exporting, setExporting] = useState(false);
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [professionOptions, setProfessionOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [districtOptions, setDistrictOptions] = useState<any[]>([]);
  // committed
  const [searchContains, setSearchContains] = useState<string>('');
  const [selectedProfessionIds, setSelectedProfessionIds] = useState<number[]>([]);
  const [healthFrom, setHealthFrom] = useState<string | undefined>(undefined);
  const [healthTo, setHealthTo] = useState<string | undefined>(undefined);
  const [isgFrom, setIsgFrom] = useState<string | undefined>(undefined);
  const [isgTo, setIsgTo] = useState<string | undefined>(undefined);
  const [employmentStartFrom, setEmploymentStartFrom] = useState<string | undefined>(undefined);
  const [employmentStartTo, setEmploymentStartTo] = useState<string | undefined>(undefined);
  const [employmentEndFrom, setEmploymentEndFrom] = useState<string | undefined>(undefined);
  const [employmentEndTo, setEmploymentEndTo] = useState<string | undefined>(undefined);
  const [stateEq, setStateEq] = useState<number | undefined>(undefined);
  const [districtEq, setDistrictEq] = useState<number | undefined>(undefined);
  const [hasLicense, setHasLicense] = useState<'all' | 'yes' | 'no'>('all');
  const [licenseClasses, setLicenseClasses] = useState<string[]>([]);
  const [enabledStatus, setEnabledStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [withoutDeviceFilter, setWithoutDeviceFilter] = useState<boolean | undefined>(undefined);
  // quick search
  const [quickSearch, setQuickSearch] = useState<string>('');
  const DEFAULT_SORT_COLUMN = 'employmentStartDate';
  const DEFAULT_SORT_ORDER: SortOrder = 'ascend';
  const [sortState, setSortState] = useState<{ columnKey?: string; order?: SortOrder }>({
    columnKey: DEFAULT_SORT_COLUMN,
    order: DEFAULT_SORT_ORDER
  });
  // pending
  const [pProfessionIds, setPProfessionIds] = useState<number[]>([]);
  const [pHealthFrom, setPHealthFrom] = useState<any>(null);
  const [pHealthTo, setPHealthTo] = useState<any>(null);
  const [pIsgFrom, setPIsgFrom] = useState<any>(null);
  const [pIsgTo, setPIsgTo] = useState<any>(null);
  const [pEmploymentStartFrom, setPEmploymentStartFrom] = useState<any>(null);
  const [pEmploymentStartTo, setPEmploymentStartTo] = useState<any>(null);
  const [pEmploymentEndFrom, setPEmploymentEndFrom] = useState<any>(null);
  const [pEmploymentEndTo, setPEmploymentEndTo] = useState<any>(null);
  const [pStateEq, setPStateEq] = useState<number | undefined>(undefined);
  const [pDistrictEq, setPDistrictEq] = useState<number | undefined>(undefined);
  const [pHasLicense, setPHasLicense] = useState<'all' | 'yes' | 'no'>('all');
  const [pLicenseClasses, setPLicenseClasses] = useState<string[]>([]);
  const [pEnabledStatus, setPEnabledStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [pWithoutDevice, setPWithoutDevice] = useState(false);
  const [pWithoutDeviceApplied, setPWithoutDeviceApplied] = useState(false);

  const drivingLicenseClassMap: Record<string, string> = { "NONE": "Yok", "M": "M Sınıfı", "A1": "A1 Sınıfı", "A2": "A2 Sınıfı", "A": "A Sınıfı", "B1": "B1 Sınıfı", "B": "B Sınıfı", "BE": "BE Sınıfı", "C1": "C1 Sınıfı", "C1E": "C1E Sınıfı", "C": "C Sınıfı", "CE": "CE Sınıfı", "D1": "D1 Sınıfı", "D1E": "D1E Sınıfı", "D": "D Sınıfı", "DE": "DE Sınıfı", "F": "F Sınıfı", "G": "G Sınıfı" };
  const resolveSortParam = useCallback(() => {
    if (!sortState.columnKey || !sortState.order) return undefined;
    const direction = sortState.order === 'ascend' ? 'asc' : 'desc';
    return `${sortState.columnKey},${direction}`;
  }, [sortState.columnKey, sortState.order]);
  const location = useLocation();
  const isInspectorsPage = location.pathname === '/settings/inspectors' || location.pathname === '/inspectors';
  const wasInspectorsPageRef = useRef<boolean>(isInspectorsPage);
  const resetState = useCallback(() => {
    setData([]);
    setTotal(0);
    setPage(1);
    setPageSize(10);
    setSortState({ columnKey: DEFAULT_SORT_COLUMN, order: DEFAULT_SORT_ORDER });
    setSearchContains('');
    setQuickSearch('');
    setSelectedProfessionIds([]);
    setHealthFrom(undefined);
    setHealthTo(undefined);
    setIsgFrom(undefined);
    setIsgTo(undefined);
    setEmploymentStartFrom(undefined);
    setEmploymentStartTo(undefined);
    setEmploymentEndFrom(undefined);
    setEmploymentEndTo(undefined);
    setStateEq(undefined);
    setDistrictEq(undefined);
    setHasLicense('all');
    setLicenseClasses([]);
    setEnabledStatus('all');
    setWithoutDeviceFilter(undefined);
    setPProfessionIds([]);
    setPHealthFrom(null);
    setPHealthTo(null);
    setPIsgFrom(null);
    setPIsgTo(null);
    setPEmploymentStartFrom(null);
    setPEmploymentStartTo(null);
    setPEmploymentEndFrom(null);
    setPEmploymentEndTo(null);
    setPStateEq(undefined);
    setPDistrictEq(undefined);
    setPHasLicense('all');
    setPLicenseClasses([]);
    setPEnabledStatus('all');
    setPWithoutDevice(false);
    setPWithoutDeviceApplied(false);
    setOpen(false);
    setFilterOpen(false);
    setEditingId(undefined);
    setAccreditationSwitchLoadingId(null);
  }, [DEFAULT_SORT_COLUMN, DEFAULT_SORT_ORDER]);
  useEffect(() => {
    if (isInspectorsPage && !wasInspectorsPageRef.current) {
      resetState();
    }
    wasInspectorsPageRef.current = isInspectorsPage;
  }, [isInspectorsPage, resetState]);

  const buildFilterQuery = useCallback(() => {
    const filters: string[] = [];
    if (searchContains) filters.push('search.contains=' + encodeURIComponent(searchContains));
    if (enabledStatus === 'active') filters.push('account.enabled.equals=true');
    if (enabledStatus === 'inactive') filters.push('account.enabled.equals=false');
    if (selectedProfessionIds.length) {
      selectedProfessionIds.forEach(id => filters.push('professionId.in=' + id));
    }
    if (healthFrom) filters.push('healthCheckValidUntil.greaterThanOrEqual=' + healthFrom);
    if (healthTo) filters.push('healthCheckValidUntil.lessThanOrEqual=' + healthTo);
    if (isgFrom) filters.push('occupationalSafetyTrainingValidUntil.greaterThanOrEqual=' + isgFrom);
    if (isgTo) filters.push('occupationalSafetyTrainingValidUntil.lessThanOrEqual=' + isgTo);
    if (employmentStartFrom) filters.push('hireDate.greaterThanOrEqual=' + employmentStartFrom);
    if (employmentStartTo) filters.push('hireDate.lessThanOrEqual=' + employmentStartTo);
    if (employmentEndFrom) filters.push('terminationDate.greaterThanOrEqual=' + employmentEndFrom);
    if (employmentEndTo) filters.push('terminationDate.lessThanOrEqual=' + employmentEndTo);
    if (stateEq) filters.push('stateId.equals=' + stateEq);
    if (districtEq) filters.push('districtId.equals=' + districtEq);
    if (hasLicense === 'yes') {
      filters.push('drivingLicenseClass.specified=true');
      if (licenseClasses.length) {
        licenseClasses.forEach(cls => filters.push('drivingLicenseClass.in=' + encodeURIComponent(cls)));
      }
    } else if (hasLicense === 'no') {
      filters.push('drivingLicenseClass.specified=false');
    }
    if (typeof withoutDeviceFilter === 'boolean') {
      filters.push('withoutDevice.equals=' + withoutDeviceFilter);
    }
    return filters.join('&');
  }, [districtEq, employmentEndFrom, employmentEndTo, employmentStartFrom, employmentStartTo, enabledStatus, hasLicense, healthFrom, healthTo, isgFrom, isgTo, licenseClasses, searchContains, selectedProfessionIds, stateEq, withoutDeviceFilter]);
  const fetchData = useCallback(async () => {
    const query = buildFilterQuery();
    const sortParam = resolveSortParam();
    const res = await listInspectors(page - 1, pageSize, sortParam, query);
    setData(res.content);
    setTotal(res.totalElements);
  }, [buildFilterQuery, page, pageSize, resolveSortParam]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { listProfessionsAll().then(setProfessionOptions); }, []);
  useEffect(() => { apiFetch('/countries/225/states').then(setStateOptions); }, []);
  // dependent districts load in filter drawer as pStateEq changes
  useEffect(() => {
    if (pStateEq) {
      apiFetch(`/states/${pStateEq}/districts`).then(setDistrictOptions);
    } else {
      setDistrictOptions([]);
    }
  }, [pStateEq]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id: number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const openFilter = () => {
    setPEnabledStatus(enabledStatus);
    setPProfessionIds(selectedProfessionIds);
    setPHealthFrom(healthFrom ? dayjs(healthFrom) : null);
    setPHealthTo(healthTo ? dayjs(healthTo) : null);
    setPIsgFrom(isgFrom ? dayjs(isgFrom) : null);
    setPIsgTo(isgTo ? dayjs(isgTo) : null);
    setPEmploymentStartFrom(employmentStartFrom ? dayjs(employmentStartFrom) : null);
    setPEmploymentStartTo(employmentStartTo ? dayjs(employmentStartTo) : null);
    setPEmploymentEndFrom(employmentEndFrom ? dayjs(employmentEndFrom) : null);
    setPEmploymentEndTo(employmentEndTo ? dayjs(employmentEndTo) : null);
    setPStateEq(stateEq);
    setPDistrictEq(districtEq);
    setPHasLicense(hasLicense);
    setPLicenseClasses(licenseClasses);
    if (typeof withoutDeviceFilter === 'boolean') {
      setPWithoutDevice(withoutDeviceFilter);
      setPWithoutDeviceApplied(true);
    } else {
      setPWithoutDevice(false);
      setPWithoutDeviceApplied(false);
    }
    setFilterOpen(true);
  };
  const closeFilter = () => setFilterOpen(false);
  const clearFilter = () => {
    setPEnabledStatus('all');
    setPProfessionIds([]);
    setPHealthFrom(null); setPHealthTo(null);
    setPIsgFrom(null); setPIsgTo(null);
    setPEmploymentStartFrom(null); setPEmploymentStartTo(null);
    setPEmploymentEndFrom(null); setPEmploymentEndTo(null);
    setPStateEq(undefined); setPDistrictEq(undefined);
    setPHasLicense('all'); setPLicenseClasses([]);
    setPWithoutDevice(false);
    setPWithoutDeviceApplied(false);
  };
  const handleDelete = async (id: number) => {
    try {
      await deleteInspector(id);
      openNotification('success', 'Başarılı', 'P.K.M Uzmanı Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'P.K.M Uzmanı silme işlemi başarısız');
    }
  };
  const activeSortKey = sortState.columnKey;
  const activeSortOrder = sortState.order;
  const resolveSortOrder = (key: string) => (activeSortKey === key ? activeSortOrder : undefined);
  const formatDateCell = (v?: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '';
  const handleExportExcel = useCallback(async () => {
    try {
      setExporting(true);
      const query = buildFilterQuery();
      const list = await listInspectorsAll(query, { includeInactive: enabledStatus !== 'active' });
      const headers = [
        'TC Kimlik No',
        'Ad Soyad',
        'İşe Giriş Tarihi',
        'İl / İlçe',
        'Doğum Tarihi',
        'Meslek',
        'Mezuniyet Tarihi',
        'Kayıt Numarası',
        'Kayıt Tarihi',
        'Telefon',
        'E-posta',
        'Ehliyet Sınıfları',
        'İşten Çıkış Tarihi',
      ];
      const rows = Array.isArray(list) ? list.map((item: any) => {
        const account = item?.account || {};
        const professions = Array.isArray(item?.professions) ? item.professions : [];
        const professionNames = professions
          .map((p: any) => (typeof p === 'object' ? p?.name : p))
          .filter(Boolean)
          .map((n: any) => String(n));
        const registrationNumbers = professions
          .map((p: any) => (typeof p === 'object' ? p?.registrationNumber : undefined))
          .filter(Boolean)
          .map((n: any) => String(n));
        const registrationDates = professions
          .map((p: any) => (typeof p === 'object' ? p?.registrationDate : undefined))
          .filter(Boolean)
          .map((d: any) => formatDateCell(d));
        const graduationDates = professions
          .map((p: any) => (typeof p === 'object' ? p?.graduationDate : undefined))
          .filter(Boolean)
          .map((d: any) => formatDateCell(d));
        const stateName = typeof item?.city === 'object' ? (item?.city?.name ?? item?.city?.label ?? '') : (item?.city ?? '');
        const districtName = typeof item?.districtObj === 'object' ? (item?.districtObj?.name ?? item?.districtObj?.label ?? '') : (item?.district ?? '');
        const locationText = [stateName, districtName].filter((s: any) => !!String(s).trim()).join(' / ');
        const licenseList = Array.isArray(item?.drivingLicenseClasses) ? item.drivingLicenseClasses : (item?.drivingLicenseClass ? [item.drivingLicenseClass] : []);
        return [
          item?.identityNumber ?? '',
          `${account?.firstName || ''} ${account?.lastName || ''}`.trim(),
          formatDateCell(item?.hireDate),
          locationText,
          formatDateCell(item?.birthDate),
          professionNames.join(', '),
          graduationDates.join(', '),
          registrationNumbers.join(', '),
          registrationDates.join(', '),
          account?.phoneNumber ?? account?.phone ?? '',
          account?.email ?? '',
          licenseList.join(', '),
          formatDateCell(item?.terminationDate),
        ];
      }) : [];
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'PKM Uzmanlari');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, 'pkm-uzmanlari.xlsx');
    } catch (error) {
      console.error('Inspectors excel export error:', error);
      openNotification('error', 'Hata', 'Excel dışa aktarılırken bir hata oluştu');
    } finally {
      setExporting(false);
    }
  }, [buildFilterQuery, enabledStatus]);
  const columns = [
    { title: 'Kimlik No', dataIndex: 'identityNumber', key: 'identityNumber', sorter: true, sortOrder: resolveSortOrder('identityNumber') },
    { title: 'Ad Soyad', dataIndex: 'account.firstName', key: 'fullName', sorter: true, sortOrder: resolveSortOrder('fullName'), render: (_: any, r: any) => r?.account?.firstName + ' ' + r?.account?.lastName },
    {
      title: 'Meslek', dataIndex: 'professions', key: 'professions', render: (_: any, r: any) => {
        const list = Array.isArray(r?.professions) ? r.professions : [];
        const names = list
          .map((p: any) => (typeof p === 'object' ? p?.name : p))
          .filter((n: any) => !!n)
          .map((n: any) => String(n));
        if (!names.length) return '';
        const sorted = names.slice().sort((a: string, b: string) => a.localeCompare(b, 'tr'));
        return (
          <Space size={4} wrap>
            {sorted.map((n: string) => (
              <Tag key={n} color='processing'>{n}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Kayıt Numarası',
      key: 'registrationNumbers',
      width: 180,
      render: (_: any, r: any) => {
        const list = Array.isArray(r?.professions) ? r.professions : [];
        const numbers: { number?: string; name?: string }[] = list
          .map((p: any) => ({
            number: typeof p === 'object' ? p?.registrationNumber : undefined,
            name: typeof p === 'object' ? p?.name : undefined,
          }))
          .filter((entry: { number?: string }) => !!entry.number);
        if (!numbers.length) return '';
        return (
          <Space size={4} wrap>
            {numbers.map((entry: { number?: string; name?: string }, idx: number) => (
              <>
                {entry.number}
                </>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'İl/İlçe',
      key: 'location',
      sorter: true,
      sortOrder: resolveSortOrder('location'),
      render: (_: any, r: any) => {
        const stateName = typeof r?.city === 'object' ? (r?.city?.name ?? r?.city?.label ?? '') : (r?.city ?? '');
        const districtName = typeof r?.districtObj === 'object' ? (r?.districtObj?.name ?? r?.districtObj?.label ?? '') : (r?.district ?? '');
        const s = String(stateName || '').trim();
        const d = String(districtName || '').trim();
        if (!s && !d) return '';
        if (s && d) return `${s} / ${d}`;
        return s || d;
      }
    },
    { title: 'Doğum Tarihi', dataIndex: 'birthDate', key: 'birthDate', sorter: true, sortOrder: resolveSortOrder('birthDate'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'İşe Giriş Tarihi', dataIndex: 'hireDate', key: 'employmentStartDate', sorter: true, sortOrder: resolveSortOrder('employmentStartDate'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'Sağlık Muayenesi Geçerlilik', dataIndex: 'healthCheckValidUntil', key: 'healthCheckValidUntil', sorter: true, sortOrder: resolveSortOrder('healthCheckValidUntil'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'İş Güvenliği Temel Eğitim Geçerlilik', dataIndex: 'occupationalSafetyTrainingValidUntil', key: 'occupationalSafetyTrainingValidUntil', sorter: true, sortOrder: resolveSortOrder('occupationalSafetyTrainingValidUntil'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'İşten Çıkış Tarihi', dataIndex: 'terminationDate', key: 'terminationDate', sorter: true, sortOrder: resolveSortOrder('terminationDate'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    {
      title: 'Akreditasyon Denetleyicisi',
      key: 'accreditationValidator',
      render: (_: any, r: any) => {
        const checked = !!r?.accreditationValidator;
        return (
          <Switch
            checked={checked}
            loading={accreditationSwitchLoadingId === r.id}
            onChange={async (val) => {
              try {
                setAccreditationSwitchLoadingId(r.id);
                await updateInspectorAccreditationValidator(r.id, val);
                await fetchData();
                openNotification('success', 'Başarılı', val ? 'Akreditasyon denetleyicisi aktif edildi' : 'Akreditasyon denetleyicisi pasif edildi');
              } catch (e) {
                openNotification('error', 'Hata', 'Akreditasyon denetleyicisi güncellenemedi.');
              } finally {
                setAccreditationSwitchLoadingId(null);
              }
            }}
          />
        );
      }
    },
    {
      title: 'Durum', key: 'status', sorter: true, sortOrder: resolveSortOrder('status'), render: (_: any, r: any) => {
        const enabled = !!(r?.account?.enabled);
        return (
          <Switch
            checkedChildren='Aktif'
            unCheckedChildren='Pasif'
            checked={enabled}
            onChange={async (val) => {
              try {
                await updateInspectorStatus(r.id, val);
                setData(prev => prev.map(item => item.id === r.id ? ({ ...item, account: { ...(item.account || {}), enabled: val } }) : item));
                openNotification('success', 'Başarılı', val ? 'Uzman aktif yapıldı' : 'Uzman pasif yapıldı');
              } catch (e) {
                openNotification('error', 'Hata', 'Durum güncellenemedi. Lütfen daha sonra tekrar deneyin.');
              }
            }}
          />
        );
      }
    },
    {
      title: 'İşlemler', key: 'actions', render: (_: any, r: any) => (
        <Space>
          <Tooltip title='Düzenle'>
            <Link to={'/inspectors/' + r.id}>
              <Button
                icon={<img src={PenIcon} alt="pen" className="icon" />}
                shape="circle"
                className="icon-button"
              />
            </Link>
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button ml-12px"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];
  const handleTableChange: TableProps<any>['onChange'] = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrder = (normalizedSorter?.order as SortOrder | undefined) ?? undefined;
    const rawKey = (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined;
    const nextKey = nextOrder ? rawKey : undefined;
    const prevKey = sortState.columnKey ?? undefined;
    const prevOrder = sortState.order ?? undefined;
    if ((prevKey ?? '') === (nextKey ?? '') && prevOrder === nextOrder) {
      return;
    }
    setSortState({ columnKey: nextKey, order: nextOrder });
    setPage(1);
  };
  return (
    <>

      <Card
        className='br-30px kontrolx-card'
        title={(
          <Row align='bottom'>
            <Typography.Title level={4} style={{ margin: 0 }}>P.K.M Uzmanları Listesi</Typography.Title>
            <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Typography.Text>
          </Row>
        )}
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-filter'
              style={{ width: '100%', maxWidth: 200 }}
              value={quickSearch}
              onChange={(e) => {
                const v = e.target.value || '';
                setQuickSearch(v);
                setSearchContains(v);
              }}
            />
            <Tooltip title="Dışa Aktar">
              <Button
                shape='circle'
                className='excel-icon-button'
                onClick={handleExportExcel}
                aria-label='Excel indir'
              >
                <FileExcelOutlined />
              </Button>
            </Tooltip>
            <Button type='primary' onClick={openFilter}>Filtrele</Button>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          onChange={handleTableChange}
          pagination={{ current: page, pageSize, total, onChange: (p, s) => { setPage(p); setPageSize(s); } }}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={1000} title={editingId ? 'P.K.M Uzmanı Düzenle' : 'P.K.M Uzmanı Ekle'}>
        <InspectorForm id={editingId} onSuccess={() => { handleClose(); fetchData(); }} />
      </Drawer>
      <Drawer open={filterOpen} onClose={closeFilter} destroyOnClose width={480} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Meslek</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 6 }}
              value={pProfessionIds}
              onChange={(vals: number[]) => setPProfessionIds(vals)}
              showSearch
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            >
              {professionOptions.map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>İşe Giriş Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pEmploymentStartFrom, pEmploymentStartTo] as any}
              onChange={(vals) => { setPEmploymentStartFrom(vals?.[0] || null); setPEmploymentStartTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
              allowClear
            />
          </div>
          <div>
            <Typography.Text>İşten Çıkış Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pEmploymentEndFrom, pEmploymentEndTo] as any}
              onChange={(vals) => { setPEmploymentEndFrom(vals?.[0] || null); setPEmploymentEndTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
              allowClear
            />
          </div>
          <div>
            <Typography.Text>Sağlık Muayenesi Geçerlilik Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pHealthFrom, pHealthTo] as any}
              onChange={(vals) => { setPHealthFrom(vals?.[0] || null); setPHealthTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
            />
          </div>
          <div>
            <Typography.Text>İSG Temel Eğitim Geçerlilik Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pIsgFrom, pIsgTo] as any}
              onChange={(vals) => { setPIsgFrom(vals?.[0] || null); setPIsgTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
            />
          </div>
          <div>
            <Typography.Text>İl</Typography.Text>
            <Select
              allowClear
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 6 }}
              value={pStateEq}
              onChange={(v) => { setPStateEq(v); setPDistrictEq(undefined); }}
              showSearch
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            >
              {stateOptions.map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>İlçe</Typography.Text>
            <Select
              allowClear
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 6 }}
              value={pDistrictEq}
              onChange={(v) => setPDistrictEq(v)}
              disabled={!pStateEq}
              showSearch
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            >
              {districtOptions.map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>Ehliyet</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={pHasLicense}
              onChange={(val: 'all' | 'yes' | 'no') => setPHasLicense(val)}
              options={[{ value: 'all', label: 'Tümü' }, { value: 'yes', label: 'Var' }, { value: 'no', label: 'Yok' }]}
              showSearch
              optionFilterProp='label'
              filterOption={(input, option) => (String((option as any)?.label || '')).toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            />
          </div>
          {pHasLicense === 'yes' && (
            <div>
              <Typography.Text>Ehliyet Sınıfları</Typography.Text>
              <Select
                mode='multiple'
                allowClear
                placeholder='Seçiniz'
                style={{ width: '100%', marginTop: 6 }}
                value={pLicenseClasses}
                onChange={(vals: string[]) => setPLicenseClasses(vals)}
                showSearch
                filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
              >
                {Object.keys(drivingLicenseClassMap).filter(k => k !== 'NONE').map(k => (
                  <Select.Option key={k} value={k}>{drivingLicenseClassMap[k]}</Select.Option>
                ))}
              </Select>
            </div>
          )}
          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={pEnabledStatus}
              onChange={(val: 'all' | 'active' | 'inactive') => setPEnabledStatus(val)}
              options={[{ value: 'all', label: 'Tümü' }, { value: 'active', label: 'Aktif' }, { value: 'inactive', label: 'Pasif' }]}
              showSearch
              optionFilterProp='label'
              filterOption={(input, option) => (String((option as any)?.label || '')).toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            />
          </div>
          <div>
            <Typography.Text>Cihaz olmayan</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Switch
                checked={pWithoutDevice}
                onChange={(val) => {
                  setPWithoutDevice(val);
                  setPWithoutDeviceApplied(true);
                }}
              />
            </div>
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%', marginTop: 8 }}>
            <Button onClick={clearFilter}>Temizle</Button>
            <Button type='primary' onClick={() => {
              setEnabledStatus(pEnabledStatus);
              setSelectedProfessionIds(pProfessionIds);
              setHealthFrom(pHealthFrom ? pHealthFrom.format('YYYY-MM-DD') : undefined);
              setHealthTo(pHealthTo ? pHealthTo.format('YYYY-MM-DD') : undefined);
              setIsgFrom(pIsgFrom ? pIsgFrom.format('YYYY-MM-DD') : undefined);
              setIsgTo(pIsgTo ? pIsgTo.format('YYYY-MM-DD') : undefined);
              setEmploymentStartFrom(pEmploymentStartFrom ? pEmploymentStartFrom.format('YYYY-MM-DD') : undefined);
              setEmploymentStartTo(pEmploymentStartTo ? pEmploymentStartTo.format('YYYY-MM-DD') : undefined);
              setEmploymentEndFrom(pEmploymentEndFrom ? pEmploymentEndFrom.format('YYYY-MM-DD') : undefined);
              setEmploymentEndTo(pEmploymentEndTo ? pEmploymentEndTo.format('YYYY-MM-DD') : undefined);
              setStateEq(pStateEq);
              setDistrictEq(pDistrictEq);
              setHasLicense(pHasLicense);
              setLicenseClasses(pLicenseClasses);
              setWithoutDeviceFilter(pWithoutDeviceApplied ? pWithoutDevice : undefined);
              closeFilter();
            }}>Filtrele</Button>
          </Space>
        </Space>
      </Drawer>
    </>
  );
}
